import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { DUR, EASE } from "@/lib/motion";
import { Initials, scoreTone } from "@/components/job/Lifecycle";
import { Flag, ShieldCheck, UserCheck, MoreHorizontal } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

export const STAGES = [
  { key: "applied", label: "Applied" },
  { key: "screening", label: "Screening" },
  { key: "interview", label: "Interview" },
  { key: "offer", label: "Offer" },
  { key: "hired", label: "Hired" },
  { key: "rejected", label: "Not progressing" },
];

export const ageLabel = (c) => {
  const a = c.stage_age;
  if (a?.days == null) return null;
  if (a.closed) return `Closed ${a.days}d ago`;
  return `${a.source === "application_estimate" ? "~" : ""}${a.days < 1 ? "<1" : a.days}d in stage`;
};
export const testLabel = (c) => c.objective_score != null ? `Test ${c.objective_score}` : c.assessment?.status === "completed" ? "Assessed" : c.assessment_active ? "Testing now" : c.assessment ? "Test sent" : "Not started";
const Tag = ({ children, className, ...p }) => <span className={cn("text-[11px] px-2 py-0.5 rounded-md bg-foreground/[0.06] text-muted-foreground", className)} {...p}>{children}</span>;

export function MoveMenu({ cand, onMove, trigger }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44" onClick={(e) => e.stopPropagation()}>
        <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">Move to</DropdownMenuLabel>
        {STAGES.filter((s) => s.key !== cand.stage).map((s) => <DropdownMenuItem key={s.key} onClick={() => onMove(cand, s.key)} data-testid={`move-${cand.id}-${s.key}`}>{s.label}</DropdownMenuItem>)}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function BoardCard({ c, selected, onToggle, onOpen, onMove, reduce }) {
  const headline = c.parsed_profile?.headline && c.parsed_profile.headline !== c.name ? c.parsed_profile.headline : c.email;
  const age = ageLabel(c);
  return (
    <motion.div role="button" tabIndex={0} aria-label={`Review ${c.name}`} onClick={() => onOpen(c)} data-testid={`candidate-card-${c.id}`} data-compare-selected={selected}
      layout={!reduce} layoutId={reduce ? undefined : `pcard-${c.id}`}
      initial={reduce ? false : { opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: DUR.base, ease: EASE.out }}
      onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); onOpen(c); } }}
      className={cn("group rounded-xl border bg-card p-3.5 space-y-3 cursor-pointer transition-[border-color,box-shadow] duration-150 hover:shadow-lg hover:shadow-black/20", selected ? "border-primary/60 ring-1 ring-primary/30" : "border-border hover:border-foreground/20")}>
      <div className="flex items-center gap-2.5">
        <button onClick={(e) => { e.stopPropagation(); onToggle(c.id); }} data-testid={`compare-check-${c.id}`} aria-pressed={selected} aria-label={selected ? `Deselect ${c.name}` : `Select ${c.name}`}
          className={cn("h-4 w-4 rounded border shrink-0 transition-colors", selected ? "bg-primary border-primary" : "border-foreground/25 hover:border-primary")} />
        <Initials name={c.name} />
        <span className="min-w-0 flex-1 leading-tight">
          <span className="block text-sm font-semibold truncate">{c.name}</span>
          <span className="block text-xs text-muted-foreground truncate">{headline}</span>
        </span>
        <span className={cn("text-[15px] font-bold tabular-nums", scoreTone(c.overall_score))} data-testid={`candidate-score-${c.id}`}>{c.overall_score ?? "—"}</span>
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <Tag>{testLabel(c)}</Tag>
        {age && <Tag title="Time in this stage" data-testid={`stage-aging-${c.id}`}>{age}</Tag>}
        {c.violations?.length > 0 && <Tag className="text-amber-600 dark:text-amber-300 bg-amber-400/10"><Flag className="h-3 w-3 inline -mt-0.5 mr-0.5" />{c.violations.length}</Tag>}
        {c.integrity && (c.assessment_active || c.integrity.flags > 0) && <Tag data-testid={`integrity-${c.id}`}><ShieldCheck className="h-3 w-3 inline -mt-0.5 mr-0.5" />{c.integrity.score}</Tag>}
        {c.human_review && <Tag className="text-primary bg-primary/10"><UserCheck className="h-3 w-3 inline -mt-0.5 mr-0.5" />reviewed</Tag>}
        <span className="flex-1" />
        <MoveMenu cand={c} onMove={onMove} trigger={<button className="opacity-60 group-hover:opacity-100 p-1 rounded hover:bg-foreground/[0.06]" aria-label={`Move ${c.name}`} data-testid={`move-${c.id}`}><MoreHorizontal className="h-4 w-4" /></button>} />
      </div>
    </motion.div>
  );
}
